/**
 * chartsHelpers.jsx
 * Utilities for managing and displaying climate charts
 * Migration from front/src/chartsHelpers.js to modular ES6 architecture
 */
import React from 'react';
import { scaleLinear, scaleBand, scalePoint } from 'd3-scale';
import { indicatorsConfiguration } from '../config/settings.js';

// ===== DATA EXAMPLES =====

export const entityDataExample = [
  { type_ind: 'annuel', label: null, ref: 5, min: 6, mean: 10, max: 11 },
  /*
  { type_ind: 'saisonnier', label: 'Été', ref: 8, min: 11, mean: 13, max: 14 },
  { type_ind: 'saisonnier', label: 'Automne', ref: 1, min: 2, mean: 3, max: 7 },
  { type_ind: 'saisonnier', label: 'Hiver', ref: 3, min: 2, mean: 5, max: 6 },
  { type_ind: 'saisonnier', label: 'Printemps', ref: 9, min: null, mean: 13, max: null },
  */
];

// ===== CHART CONFIGURATION =====

export const circleSizeConfig = {
  annual: 30,
  season: 23,
};

const lineWidthConfig = {
  annual: 2,
  season: 1,
};

const textSizeConfig = {
  annual: 19,
  season: 15,
};

export const xPositions = ['ref', 'label', 'target'];

// ===== DATA UTILITIES =====

/**
 * Extracts seasons from indicator data
 * @param {Array} data - Indicator data
 * @returns {Array} List of seasons or types
 */
export const getSeasons = (data) =>
  data.flatMap(({ label, type_ind: type }) => label || type);

/**
 * Gets color palette for an indicator
 * @param {string} indicatorKey - Indicator key
 * @returns {Object|undefined} Color configuration
 */
export const getIndicatorColorPalette = (indicatorKey = 'G1') =>
  indicatorsConfiguration[indicatorKey]?.color;

/**
 * Gets numeric value transformation for Y-axis scale calculations
 * Falls back to valueRounding for backward compatibility
 * @param {string} indicatorKey - Indicator key
 * @returns {Function} Formatting function (always returns NUMBER)
 */
export const getIndicatorValueForScale = (indicatorKey = 'G1') => {
  const config = indicatorsConfiguration[indicatorKey];
  return config?.valueForScale || (value => value);
};

/**
 * Gets value transformation for text display (shapes, tooltips)
 * Falls back to valueRounding for backward compatibility
 * @param {string} indicatorKey - Indicator key
 * @returns {Function} Formatting function (can return STRING)
 */
export const getIndicatorValueForDisplay = (indicatorKey = 'G1') => {
  const config = indicatorsConfiguration[indicatorKey];
  return config?.valueForDisplay || (value => value);
};

/**
 * Gets tooltip title for an indicator
 * @param {string} indicatorKey - Indicator key
 * @returns {string|undefined} Tooltip title
 */
export const getTooltipTitle = (indicatorKey = 'G1') =>
  indicatorsConfiguration[indicatorKey]?.tooltipTitle;

// ===== CHART CONFIGURATION =====

/**
 * Builds chart point configuration
 * @param {Object} options - Configuration options
 * @param {string} options.ref - Reference color
 * @param {string} options.min - Minimum color
 * @param {string} options.mean - Mean color
 * @param {string} options.max - Maximum color
 * @returns {Object} Point configuration
 */
export const buildConfigChartPoint = ({
  ref = '#B6B6B6',
  min = '#96D2FA',
  mean = '#6496FA',
  max = '#0A46AF',
}) => ({
  ref: { color: ref, shape: 'circle' },
  min: { color: min, shape: 'circle' },
  mean: { color: mean, shape: 'circle' },
  max: { color: max, shape: 'circle' },
});

/**
 * Generates legend configuration
 * @param {Object} config - Color configuration
 * @param {Array} legendItems - Legend items
 * @returns {Array} Configured legends
 */
export const getLegends = (config, legendItems = []) =>
  legendItems.map(item => ({
    ...item,
    color: config[item.type]?.color || 'grey'
  }));

// ===== D3 SCALES =====

/**
 * Creates a D3 linear scale
 * @param {Array} range - Scale range
 * @param {Array} domain - Scale domain
 * @returns {Function} Linear scale
 */
export const getLinearScale = (range, domain) =>
  scaleLinear()
    .range(range)
    .domain(domain);

/**
 * Creates a D3 point scale
 * @param {Array} range - Scale range
 * @param {Array} domain - Scale domain
 * @param {number} padding - Padding
 * @param {boolean} round - Round values
 * @returns {Function} Point scale
 */
export const getPointScale = (range, domain, padding = 0.1, round = true) =>
  scalePoint()
    .domain(domain)
    .range(range)
    .padding(padding)
    .round(round);

/**
 * Creates a D3 band scale
 * @param {Array} range - Scale range
 * @param {Array} domain - Scale domain
 * @param {number} paddingInner - Inner padding
 * @param {number} paddingOuter - Outer padding
 * @returns {Function} Band scale
 */
export const getBandScale = (range, domain, paddingInner = 0.4, paddingOuter = 0.1) =>
  scaleBand()
    .domain(domain)
    .range(range)
    .paddingInner(paddingInner)
    .paddingOuter(paddingOuter);

// ===== SVG COMPONENTS =====

/**
 * Generates an SVG circle for charts
 * @param {Object} params - Circle parameters
 * @param {string|number} params.x - X position
 * @param {number} params.value - Y value
 * @param {string} params.season - Season/axis
 * @param {Function} params.xScale - X scale
 * @param {Function} params.yScale - Y scale
 * @param {string} params.color - Circle color
 * @param {number} params.r - Circle radius
 * @param {string} params.indicatorKey - Indicator key
 * @returns {JSX.Element} SVG circle element
 */
const circleShape = ({
  x, value, season, xScale, yScale, color, r, indicatorKey,
}) => (
  <circle
    key={`circle-${indicatorKey}-${x}-${value}-${season}`}
    cx={xScale(x, season)}
    cy={yScale(value)}
    r={r}
    fill={color}
  />
);

/**
 * Generates an SVG line for charts
 * @param {Object} params - Line parameters
 * @param {string|number} params.xStart - Start X position
 * @param {number} params.yStart - Start Y position
 * @param {string|number} params.xEnd - End X position
 * @param {number} params.yEnd - End Y position
 * @param {Function} params.xScale - X scale
 * @param {Function} params.yScale - Y scale
 * @param {string} params.axisSeason - Season/axis
 * @param {string} params.color - Line color
 * @param {string} params.indicatorKey - Indicator key
 * @returns {JSX.Element} SVG line element
 */
const line = ({
  xStart, yStart, xEnd, yEnd, xScale, yScale, axisSeason, color, indicatorKey,
}) => {
  const lineWidth = ['singleAxis', 'annuel'].includes(axisSeason)
    ? lineWidthConfig.annual
    : lineWidthConfig.season;

  return (
    <line
      key={`chart-line-${indicatorKey}-${xStart}-${yStart}-${xEnd}-${yEnd}-${color}`}
      x1={xScale(xStart, axisSeason)}
      y1={yScale(yStart)}
      x2={xScale(xEnd, axisSeason)}
      y2={yScale(yEnd)}
      stroke={color}
      strokeWidth={lineWidth}
    />
  );
};

/**
 * Generates simple SVG text (no line wrapping) for chart values
 * Used for numeric values on data points
 * @param {Object} params - Text parameters
 * @param {string|number} params.x - X position
 * @param {number} params.y - Y position
 * @param {string|number} params.text - Text to display
 * @param {string} params.season - Season/axis
 * @param {Function} params.xScale - X scale
 * @param {Function} params.yScale - Y scale
 * @param {number} params.textSize - Text size
 * @param {number} params.textyOffset - Y text offset
 * @param {string} params.fill - Text color
 * @returns {JSX.Element} SVG text element
 */
const svgTextSimple = ({
  x, y, text, season, xScale, yScale,
  textSize = 15, textyOffset = 0, fill = 'white'
}) => (
  <text
    key={`text-${x}-${y}-${text}-${season}`}
    x={xScale(x, season)}
    y={yScale(y) + textyOffset}
    fontSize={textSize}
    textAnchor="middle"
    dominantBaseline="central"
    fill={fill}
    fontWeight="bold"
  >
    {text}
  </text>
);

/**
 * Generates SVG text with line wrapping support for season labels
 * Uses foreignObject to enable HTML text wrapping
 * @param {Object} params - Text parameters
 * @param {string|number} params.x - X position
 * @param {number} params.y - Y position
 * @param {string|number} params.text - Text to display
 * @param {string} params.season - Season/axis
 * @param {Function} params.xScale - X scale
 * @param {Function} params.yScale - Y scale
 * @param {number} params.textSize - Text size
 * @param {number} params.textyOffset - Y text offset
 * @param {string} params.fill - Text color
 * @param {number} params.maxWidth - Maximum width for text wrapping
 * @returns {JSX.Element} SVG foreignObject with HTML text element
 */
export const svgText = ({
  x, y, text, season, xScale, yScale,
  textSize = 15, textyOffset = 0, fill = 'white', maxWidth = 100
}) => {
  const xPos = xScale(x, season);
  const yPos = yScale(y) + textyOffset;

  return (
    <foreignObject
      key={`text-${x}-${y}-${text}-${season}`}
      x={xPos - maxWidth / 2}
      y={yPos - textSize * 1.2}
      width={maxWidth}
      height={textSize * 2}
      style={{ overflow: 'visible' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          fontSize: `${textSize}px`,
          fontWeight: 'bold',
          color: fill,
          textAlign: 'center',
          lineHeight: 1,
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {text}
      </div>
    </foreignObject>
  );
};

// ===== RENDERING UTILITIES =====

/**
 * Draws lines for the chart
 * @param {Function} xScale - D3 x-scale function
 * @param {Function} yScale - D3 y-scale function
 * @param {string} indicatorKey - Indicator key
 * @param {Array} dataLines - Array of line data objects
 * @param {string} greyColor - Default grey color
 * @returns {Array} SVG line elements
 */
export const drawLines = (xScale, yScale, indicatorKey, dataLines = [], greyColor = 'grey') => {
  if (!Array.isArray(dataLines)) {
    return [];
  }

  return dataLines.map(({ xStart, yStart, xEnd, yEnd, color, lineWidth, axisSeason }) => {
    const lineColor = color || greyColor;
    return line({
      xStart,
      yStart,
      xEnd,
      yEnd,
      xScale,
      yScale,
      axisSeason,
      color: lineColor,
      lineWidth,
      indicatorKey,
    });
  });
};

/**
 * Text configuration based on type
 * @param {string} type - Indicator type
 * @param {string} shapeColor - Shape color
 * @param {number} offsetPx - Pixel offset
 * @returns {Object} Text configuration
 */
const getTextConfig = (type = 'ref', shapeColor = 'white', offsetPx = 15) => {
  let textOffset = 0;
  let textColor = 'white';

  if (type === 'min') {
    textOffset = offsetPx;
    textColor = shapeColor;
  }
  if (type === 'max') {
    textOffset = -offsetPx;
    textColor = shapeColor;
  }

  return { textOffset, textColor };
};

/**
 * Default identity formatter function (returns value unchanged)
 * @param {*} value - Input value
 * @returns {*} Same value unchanged
 */
const identityFormatter = value => value;

/**
 * Draws shapes on chart based on data points
 * @param {Function} xScale - D3 scale for X axis
 * @param {Function} yScale - D3 scale for Y axis
 * @param {Object} config - Shape configuration
 * @param {string} indicatorKey - Indicator identifier
 * @param {Array} dataShapes - Array of shape data (values are scaled for Y-axis)
 * @param {string} [greyColor] - Default grey color
 * @param {Function} [displayFormatter] - Display formatting function for text labels
 * @param {boolean} [isPrint] - Print mode flag
 * @returns {Array} Array of React shape components
 */
export const drawShapes = (
  xScale,
  yScale,
  config,
  indicatorKey,
  dataShapes = [],
  greyColor = 'grey',
  displayFormatter = identityFormatter,
  isPrint = false,
) =>
  dataShapes.map(({ x, type, value, axisSeason }) => {
    const shapeColor = config[type]?.color || greyColor;
    const annualSize = !isPrint && ['singleAxis', 'annuel'].includes(axisSeason);
    const shapeRadius = annualSize ? circleSizeConfig.annual : circleSizeConfig.season;
    const textSize = annualSize ? textSizeConfig.annual : textSizeConfig.season;
    const { textOffset, textColor } = getTextConfig(type, shapeColor, shapeRadius);

    return (
      <React.Fragment key={`${x}${type}${value}${axisSeason}`}>
        {circleShape({
          x,
          value,
          season: axisSeason,
          xScale,
          yScale,
          color: shapeColor,
          r: shapeRadius,
          indicatorKey,
        })}
        {svgTextSimple({
          x,
          y: value,
          text: displayFormatter(value),
          season: axisSeason,
          xScale,
          yScale,
          textSize,
          textyOffset: textOffset * 1.4,
          fill: textColor,
        })}
      </React.Fragment>
    );
  });
